import React, { FC } from 'react';
import { ifProp } from 'styled-tools';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { isSpecialPrice } from '$lib/helpers';
import { ISimpleProduct } from '$models';
import { Flex } from '$components/layouts';
import { useGtm } from '$hooks';
import { RebatePercent } from './rebate-percent';
import { SvgIcon } from '$components/elements/svg-icon';
import { Link } from '$components/elements/link';

type VariantSelectorProps = {
    variant?: ISimpleProduct;
    selectedVariantNumber?: string;
};

export const VariantSelectorMobile: FC<VariantSelectorProps> = ({
    variant,
    selectedVariantNumber,
}: VariantSelectorProps): JSX.Element => {
    const { replace } = useRouter();
    const { trackVirtualPageview } = useGtm();

    const getVariantIcon = (itemBinding?: string) => {
        if (!itemBinding) {
            return 'book';
        }
        switch (itemBinding.toLocaleLowerCase()) {
            case 'hardback':
            case 'indbundet':
            case 'hæftet':
            case 'paperback':
                return 'book';
            case 'e-bog':
            case 'ebog':
                return 'smartPhone';
            case 'lydbog':
                return 'headphones';
            case 'cd':
            case 'mp3-cd':
                return 'disc';
            default:
                return 'book';
        }
    };

    const onVariantSelect = (e: React.MouseEvent, variant: ISimpleProduct) => {
        e.preventDefault();
        trackVirtualPageview(variant?.productUrl);
        replace(variant?.productUrl || '');
    };

    return (
        <>
            {!!variant?.productUrl && (
                <VariantSelectorLink
                    href={variant.productUrl}
                    scroll={false}
                    replace
                    expansive
                    onClick={(e) => onVariantSelect(e, variant)}
                >
                    <StyledVariantSelector isSelected={selectedVariantNumber === variant?.itemNumber}>
                        <Flex gap="small" alignItems="center">
                            {!!getVariantIcon(variant?.itemBinding) && (
                                <SvgIcon size={24} svg={getVariantIcon(variant?.itemBinding)} />
                            )}
                            <ItemBinding isSelected={selectedVariantNumber === variant?.itemNumber}>
                                {variant?.itemGlobalBinding || variant?.itemBinding}
                            </ItemBinding>
                        </Flex>

                        <Flex gap="small" alignItems="center">
                            {isSpecialPrice(variant?.activePrice?.accountRelation) ? (
                                <Price isSelected={selectedVariantNumber === variant?.itemNumber} hasRebate>
                                    {variant.activeAmountAsText}
                                </Price>
                            ) : (
                                <Flex gap="extrasmall" alignItems="center">
                                    {variant.inActivePrice && (
                                        <Flex column alignItems="center">
                                            <InActivePrice>{variant.inActivePrice.amount}</InActivePrice>
                                            <RebatePercent
                                                isSelected={selectedVariantNumber === variant?.itemNumber}
                                            >{`-${Math.round(Number(variant.rebatePercent))}%`}</RebatePercent>
                                        </Flex>
                                    )}
                                    <Price
                                        isSelected={selectedVariantNumber === variant?.itemNumber}
                                        hasRebate={!!variant.inActivePrice}
                                    >
                                        {variant.activeAmountAsText}
                                    </Price>
                                </Flex>
                            )}
                            <VariantRadio selected={selectedVariantNumber === variant?.itemNumber} />
                        </Flex>
                    </StyledVariantSelector>
                </VariantSelectorLink>
            )}
        </>
    );
};

const VariantSelectorLink = styled(Link)({
    display: 'flex',
});

const StyledVariantSelector = styled.div<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
    position: 'relative',
    border: isSelected ? `2px solid ${theme.colors.primaryTheme}` : `1px solid ${theme.colors.grey5}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: theme.space[1],
    color: theme.colors.black,
    height: '100%',
    cursor: isSelected ? 'default' : 'pointer',
    borderRadius: theme.general.borderRadius,
    padding: theme.space[4],
    opacity: 1,
    transition: 'height 200ms ease-in-out, opacity 150ms ease-in-out 50ms',
    width: '100%',

    '&:hover': {
        backgroundColor: isSelected ? 'transparent' : theme.colors.grey5,
    },
}));

const Price = styled.div<{ hasRebate: boolean; isSelected: boolean }>(
    ({ theme, isSelected }) => ({
        fontWeight: isSelected ? theme.fontWeights.bold : theme.fontWeights.normal,
        fontSize: theme.fontSizes.md,
        textAlign: 'center',
    }),
    ifProp({ hasRebate: true }, ({ theme, isSelected }) => ({
        color: isSelected === false ? theme.colors.black : theme.colors.primaryTheme,
    }))
);

const InActivePrice = styled.div(({ theme }) => ({
    fontSize: theme.fontSizes.xxs,
    color: theme.colors.textSubtle,
    textDecoration: 'line-through',
    textAlign: 'center',
}));

const ItemBinding = styled.h2<{ isSelected?: boolean }>(({ isSelected, theme }) => ({
    ...theme.mixins.useTextStyle('bodyLarge'),
    marginBottom: 0,
    letterSpacing: '0',
    paddingTop: theme.space[2],
    paddingBottom: theme.space[1],
    ...(isSelected && {
        fontWeight: theme.fontWeights.bold,
    }),
}));

const VariantRadio = styled.div<{ selected?: boolean }>(({ theme, selected }) => ({
    width: '25px',
    height: '25px',
    borderRadius: theme.general.circleRadius,
    border: `1px solid ${theme.colors.grey30}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...(selected && {
        border: `1px solid ${theme.colors.black}`,
        '&:after': {
            content: '""',
            width: '11px',
            height: '11px',
            borderRadius: theme.general.circleRadius,
            backgroundColor: theme.colors.black,
        },
    }),
    ...(!selected && {
        '&:hover:after': {
            content: '""',
            width: '11px',
            height: '11px',
            borderRadius: theme.general.circleRadius,
            backgroundColor: theme.colors.grey30,
        },
    }),
}));
